/**
 * Pick one Resource for a Staffing Request — the requester's suggestion, or the RM's
 * choice at approval.
 *
 * Served by the narrow resource-options endpoint: name and Discipline only, never
 * capacity, ratings or cost — so it is safe for [ACCT], who is denied the Resource
 * directory. Results are searched server-side (debounced) and shown inline.
 */

import { X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useStaffingResourceOptions } from "@/hooks/use-staffing-requests";
import { cn } from "@/lib/utils";
import type { StaffingResourceOption } from "@/schemas/staffing-requests";

interface Props {
  solutionId: string;
  selected: StaffingResourceOption | null;
  onChange: (option: StaffingResourceOption | null) => void;
  /** Resources that may not be picked (e.g. the person being replaced). */
  excludeIds?: string[];
  /** Highlights the requester's suggestion in the list. */
  suggestedId?: string | null;
  id?: string;
}

function disciplineCodes(option: StaffingResourceOption): string {
  return option.disciplines.map((d) => d.code).join(" · ");
}

export function ResourceOptionPicker({
  solutionId,
  selected,
  onChange,
  excludeIds = [],
  suggestedId = null,
  id,
}: Props) {
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 250);
  const { data, isLoading, isError, refetch } = useStaffingResourceOptions(
    selected ? null : solutionId,
    debounced,
  );

  if (selected) {
    return (
      <div className="flex h-9 items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-sm">
        <span className="truncate">
          <span className="font-medium">{selected.name}</span>
          {selected.disciplines.length > 0 && (
            <span className="ml-2 text-xs text-muted-foreground">{disciplineCodes(selected)}</span>
          )}
        </span>
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={`Clear ${selected.name}`}
          className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  const options = (data ?? []).filter((o) => !excludeIds.includes(o.id));

  return (
    <div className="space-y-1.5">
      <Input
        id={id}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search Resources by name"
        autoComplete="off"
      />
      <div className="max-h-48 overflow-y-auto rounded-md border border-border bg-card">
        {isLoading ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">Loading…</p>
        ) : isError ? (
          <div className="flex items-center justify-between px-3 py-2 text-sm text-muted-foreground">
            Could not load Resources.
            <Button type="button" variant="ghost" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : options.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">No matching Resources.</p>
        ) : (
          <ul aria-label="Resources">
            {options.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onClick={() => onChange(o)}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-muted",
                    o.id === suggestedId && "bg-primary/10",
                  )}
                >
                  <span className="truncate font-medium">
                    {o.name}
                    {o.id === suggestedId && (
                      <span className="ml-2 text-xs font-normal text-primary">Suggested</span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {disciplineCodes(o)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
